import React, { useEffect, useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Printer } from 'lucide-react';
import { STICKER_SIZES } from './PrintLabelDialog';

// iter78q — Production-side label dialog. Mirrors PrintLabelDialog UX (size picker
// matching Item Master, copies, preview, remember-per-device) but the content is a
// Work Order / Finished Unit completion label (Serial + QR + product/BOM + build date).
//
// `target` accepts either a Work Order or a Finished Unit. We resolve a unified
// payload so the same template renders for both call sites (Production Queue and
// Finished Units list).

const STORAGE_KEY_SIZE = 'prod:wo_label_size_id';

const buildPayload = ({ wo, unit, bom, pt }) => {
  const woShort = wo?.id ? `WO-${String(wo.id).slice(0, 8).toUpperCase()}` : '';
  const woNumber = wo?.work_order_number || woShort;
  const serial = unit?.serial_number || 'PENDING';
  const productName = pt?.product_type_name || bom?.bom_name || '—';
  const bomLabel = [bom?.bom_variant_code, bom?.bom_revision ? `v${bom.bom_revision}` : null]
    .filter(Boolean)
    .join(' ');
  const builtBy = unit?.built_by_name || '';
  const buildDateRaw = unit?.build_date || wo?.completed_date || null;
  let buildDate = '';
  if (buildDateRaw) {
    try { buildDate = new Date(buildDateRaw).toLocaleDateString(); } catch { buildDate = ''; }
  }
  // QR target: serial detail if minted, else WO detail
  const qrTarget = unit?.id
    ? `${window.location.origin}/finished-units/${unit.id}`
    : (wo?.id ? `${window.location.origin}/work-orders/${wo.id}` : '');
  return { woNumber, woShort, serial, productName, bomLabel, builtBy, buildDate, qrTarget };
};

const WorkOrderLabelDialog = ({ open, onOpenChange, wo, unit, bom, pt }) => {
  // Persisted size choice — remembered per device.
  const [sizeId, setSizeId] = useState(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY_SIZE) || '4x2';
    } catch { return '4x2'; }
  });
  const [copies, setCopies] = useState(1);

  // iter78t — When the BOM has a default_label_size_id, pre-select it whenever
  // the dialog opens for a fresh WO/BOM (without overriding a user's mid-print
  // pick — we only sync when `open` flips false→true).
  useEffect(() => {
    if (open && bom?.default_label_size_id) {
      setSizeId(bom.default_label_size_id);
    }
  }, [open, bom?.default_label_size_id]);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY_SIZE, sizeId); } catch {}
  }, [sizeId]);

  const size = useMemo(
    () => STICKER_SIZES.find(s => s.id === sizeId) || STICKER_SIZES.find(s => s.id === '4x2') || STICKER_SIZES[0],
    [sizeId]
  );

  const payload = useMemo(() => buildPayload({ wo, unit, bom, pt }), [wo, unit, bom, pt]);
  const isLandscape = size.width >= size.height * 1.4;

  if (!wo && !unit) return null;

  const handlePrint = () => {
    const w = window.open('', '_blank', 'width=600,height=600');
    if (!w) {
      window.alert('Please allow pop-ups to print labels.');
      return;
    }

    // Adaptive padding identical to PrintLabelDialog
    const pad = Math.max(0.02, Math.min(0.08, Math.min(size.width, size.height) * 0.08));
    const innerW = Math.max(0.1, size.width - pad * 2);
    const innerH = Math.max(0.1, size.height - pad * 2);
    const baseQrIn = isLandscape
      ? Math.min(innerH * 0.92, innerW * 0.55)
      : Math.min(innerW * 0.7, innerH * 0.55);
    const qrPx = Math.max(24, Math.round(baseQrIn * 96));

    const qrSvg = payload.qrTarget
      ? renderToStaticMarkup(<QRCodeSVG value={payload.qrTarget} size={qrPx} level="M" includeMargin={false} />)
      : '';

    // Auto-trim by size: very small labels drop secondary fields so the serial + QR stay legible.
    const veryTiny = size.width <= 2 && size.height <= 1; // 2x1, 2x0.5, 1x1 etc.
    const small = size.width <= 2.625 && size.height <= 1.1;

    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    const productLine = !veryTiny && payload.productName !== '—'
      ? `<div class="line product" title="${esc(payload.productName)}">${esc(payload.productName)}</div>` : '';
    const bomLine = !small && payload.bomLabel
      ? `<div class="line meta">${esc(payload.bomLabel)}</div>` : '';
    const builtLine = !small && payload.builtBy
      ? `<div class="line meta">By ${esc(payload.builtBy)}</div>` : '';
    const dateLine = !veryTiny && payload.buildDate
      ? `<div class="line meta">${esc(payload.buildDate)}</div>` : '';

    const labelHtml = `
      <div class="label">
        <div class="content ${isLandscape ? 'row' : 'col'}">
          ${qrSvg ? `<div class="qr-wrap">${qrSvg}</div>` : ''}
          <div class="text-wrap">
            <div class="line serial" title="${esc(payload.serial)}">${esc(payload.serial)}</div>
            <div class="line wo">${esc(payload.woNumber)}</div>
            ${productLine}
            ${bomLine}
            ${dateLine}
            ${builtLine}
          </div>
        </div>
      </div>`;

    const copiesPerItem = Math.max(1, parseInt(copies, 10) || 1);
    const allLabels = Array.from({ length: copiesPerItem }, () => labelHtml).join('');

    w.document.write(`<!doctype html><html><head><title>WO Label - ${esc(payload.serial)}</title>
      <style>
        @page { size: ${size.width}in ${size.height}in; margin: 0; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; font-family: 'IBM Plex Sans', system-ui, sans-serif; }
        .label {
          width: ${size.width}in; height: ${size.height}in;
          padding: ${pad}in; page-break-after: always; overflow: hidden;
          display: flex; align-items: center; justify-content: center;
        }
        .content { width: 100%; height: 100%; display: flex; gap: ${Math.max(0.04, pad)}in; }
        .content.row { flex-direction: row; align-items: center; justify-content: flex-start; text-align: left; }
        .content.col { flex-direction: column; align-items: center; justify-content: center; text-align: center; }
        .qr-wrap { flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
        .qr-wrap svg { width: ${baseQrIn}in; height: ${baseQrIn}in; display: block; }
        .text-wrap { flex: 1; min-width: 0; line-height: 1.15; overflow: hidden; }
        .line { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .serial { font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 800; font-size: ${isLandscape ? Math.max(9, innerH * 14) : Math.max(9, innerW * 8)}pt; color: #0f172a; }
        .wo { font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 600; font-size: ${isLandscape ? Math.max(7, innerH * 8) : Math.max(7, innerW * 5)}pt; color: #334155; margin-top: 0.02in; }
        .product { font-size: ${isLandscape ? Math.max(7, innerH * 7) : Math.max(7, innerW * 5)}pt; color: #1e293b; margin-top: 0.03in; }
        .meta { font-size: ${Math.max(5, innerW * 3.5)}pt; color: #64748b; margin-top: 0.02in; }
      </style></head><body>
        ${allLabels}
        <script>
          window.onload = function () {
            window.focus(); window.print();
            window.onafterprint = function(){ window.close(); };
          };
        </script>
      </body></html>`);
    w.document.close();
  };

  // Preview scaling
  const PREVIEW_PX_PER_IN = 64;
  const previewW = size.width * PREVIEW_PX_PER_IN;
  const previewH = size.height * PREVIEW_PX_PER_IN;
  const previewQrPx = isLandscape
    ? Math.floor(size.height * PREVIEW_PX_PER_IN * 0.82)
    : Math.floor(size.width * PREVIEW_PX_PER_IN * 0.55);

  const veryTinyPreview = size.width <= 2 && size.height <= 1;
  const smallPreview = size.width <= 2.625 && size.height <= 1.1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="wo-label-dialog">
        <DialogHeader>
          <DialogTitle>Print Work Order Label · {payload.woNumber}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Label Size</Label>
            <Select value={sizeId} onValueChange={setSizeId}>
              <SelectTrigger className="rounded-sm" data-testid="wo-label-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STICKER_SIZES.map(s => (
                  <SelectItem key={s.id} value={s.id} data-testid={`wo-label-size-option-${s.id}`}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500">Last-used size is remembered on this device.</p>
          </div>

          <div className="space-y-2">
            <Label>Copies</Label>
            <Input
              type="number" min={1} max={50}
              value={copies}
              onChange={(e) => setCopies(e.target.value)}
              className="rounded-sm w-24 mono"
              data-testid="wo-label-copies-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Preview</Label>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 flex justify-center">
              <div
                className="bg-white border border-dashed border-slate-300 flex items-center justify-center"
                style={{ width: `${previewW}px`, height: `${previewH}px`, padding: '6px' }}
                data-testid="wo-label-preview"
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: isLandscape ? 'row' : 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    width: '100%', height: '100%',
                    textAlign: isLandscape ? 'left' : 'center',
                  }}
                >
                  {payload.qrTarget && (
                    <QRCodeSVG value={payload.qrTarget} size={previewQrPx} level="M" includeMargin={false} />
                  )}
                  <div style={{ flex: 1, minWidth: 0, lineHeight: 1.1, overflow: 'hidden' }}>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, fontSize: isLandscape ? 13 : 11, color: '#0f172a' }}>
                      {payload.serial}
                    </div>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, fontSize: isLandscape ? 10 : 9, color: '#334155' }}>
                      {payload.woNumber}
                    </div>
                    {!veryTinyPreview && payload.productName !== '—' && (
                      <div style={{ fontSize: isLandscape ? 10 : 9, color: '#1e293b' }}>{payload.productName}</div>
                    )}
                    {!smallPreview && payload.bomLabel && (
                      <div style={{ fontSize: 8, color: '#64748b' }}>{payload.bomLabel}</div>
                    )}
                    {!veryTinyPreview && payload.buildDate && (
                      <div style={{ fontSize: 8, color: '#64748b' }}>{payload.buildDate}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Page size will be set to {size.width}″ × {size.height}″ when printing.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">
            Cancel
          </Button>
          <Button
            onClick={handlePrint}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="wo-label-print-confirm-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print {parseInt(copies, 10) > 1 ? `${parseInt(copies, 10)} labels` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkOrderLabelDialog;
